import { observable, action, makeObservable, computed, runInAction } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_COMMUNICATION_SETTINGS, UPDATE_COMMUNICATION_SETTINGS_GENERAL_EMAIL } from "src/api/communicationSettings";
import {
    GET_KREDI_FLOW_COMMUNICATION_SETTINGS,
    GET_EASYCONTACT_TICKETING_COMMUNICATION_SETTINGS,
    UPSERT_CUSTOMERSETTING
} from "src/api/customer";
import { UpsertCustomersetting, UpsertCustomersettingVariables } from "src/api/generated/UpsertCustomersetting";
import { GetKrediflowCommunicationSettings } from "src/api/generated/GetKrediflowCommunicationSettings";
import { GetEasycontactTicketingCommunicationSettings } from "src/api/generated/GetEasycontactTicketingCommunicationSettings";
import {
    UpdateCommunicationSettingsGeneralEmail,
    UpdateCommunicationSettingsGeneralEmailVariables
} from "src/api/generated/UpdateCommunicationSettingsGeneralEmail";
import { GetCommunicationSettings } from "src/api/generated/GetCommunicationSettings";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import { Patterns } from "src/utils/Patterns";

export interface IEcConfirmationMail {
    de: string;
    en: string;
    fr: string;
    it: string;
}

const DEFAULT_EC_CONFIRMATION_MAIL: IEcConfirmationMail = {
    de: "",
    en: "",
    fr: "",
    it: ""
};

export interface ITicketBillingAddress {
    name: string;
    co: string;
    city: string;
    zip: string;
    street: string;
    housenumber: string;
}

const DEFAULT_TICKET_BILLING_ADDRESS: ITicketBillingAddress = {
    name: "",
    co: "",
    city: "",
    zip: "",
    street: "",
    housenumber: ""
};

export interface ICommunicationSettings {
    communicationSettingsId: number;
    generalEmail: string;
    personalEmail: string;
}

export class CommunicationSettingsStore {
    rootStore: RootStore;

    isLoadingCommunicationSettings: boolean = false;

    generalEmailError: string | undefined = undefined;
    personalEmailError: string | undefined = undefined;
    krediFlowNotifyEmailError: string | undefined = undefined;

    isLoadingKrediFlowSettings: boolean = false;
    krediFlowNotifyEmail: string = "";
    originalKrediFlowNotifyEmail: string = "";

    isLoadingEasyContactTicketingSettings: boolean = false;
    sendEmailEcAndTicketing: boolean = false;
    originalSendEmailEcAndTicketing: boolean = false;
    ecFallbackEmail: string = "";
    originalEcFallbackEmail: string = "";
    ecFallbackEmailError: string | undefined = undefined;
    ecConfirmationMail: IEcConfirmationMail = { ...DEFAULT_EC_CONFIRMATION_MAIL };
    originalEcConfirmationMail: IEcConfirmationMail = { ...DEFAULT_EC_CONFIRMATION_MAIL };
    ticketBillingEmail: string = "";
    originalTicketBillingEmail: string = "";
    ticketBillingEmailError: string | undefined = undefined;
    ticketBillingAddress: ITicketBillingAddress = { ...DEFAULT_TICKET_BILLING_ADDRESS };
    originalTicketBillingAddress: ITicketBillingAddress = { ...DEFAULT_TICKET_BILLING_ADDRESS };
    ticketDemoMode: boolean = false;
    originalTicketDemoMode: boolean = false;

    communicationSettings: ICommunicationSettings = {
        communicationSettingsId: 0,
        generalEmail: "",
        personalEmail: ""
    };

    originalCommunicationSettings: ICommunicationSettings = {
        communicationSettingsId: 0,
        generalEmail: "",
        personalEmail: ""
    };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            communicationSettings: observable,
            isLoadingCommunicationSettings: observable,
            originalCommunicationSettings: observable,
            generalEmailError: observable,
            personalEmailError: observable,

            isLoadingKrediFlowSettings: observable,
            krediFlowNotifyEmail: observable,
            originalKrediFlowNotifyEmail: observable,
            krediFlowNotifyEmailError: observable,

            isLoadingEasyContactTicketingSettings: observable,
            sendEmailEcAndTicketing: observable,
            originalSendEmailEcAndTicketing: observable,
            ecFallbackEmail: observable,
            originalEcFallbackEmail: observable,
            ecFallbackEmailError: observable,
            ecConfirmationMail: observable,
            originalEcConfirmationMail: observable,
            ticketBillingEmail: observable,
            originalTicketBillingEmail: observable,
            ticketBillingEmailError: observable,
            ticketBillingAddress: observable,
            originalTicketBillingAddress: observable,
            ticketDemoMode: observable,
            originalTicketDemoMode: observable,

            loadCommunicationSettings: action,
            setIsLoadingCommunicationSettings: action,
            setCommunicationSettings: action,
            setOriginalCommunicationSettings: action,
            updateGeneralEmail: action,
            updatePersonalEmail: action,
            saveCommunicationSettings: action,
            resetCommunicationSettings: action,

            loadKrediFlowSettings: action,
            updateKrediFlowNotifyEmail: action,
            saveKrediFlowSettings: action,
            resetKrediFlowSettings: action,

            loadEasyContactTicketingSettings: action,
            toggleSendEmailEcAndTicketing: action,
            updateEcFallbackEmail: action,
            updateEcConfirmationMail: action,
            updateTicketBillingEmail: action,
            updateTicketBillingAddress: action,
            toggleTicketDemoMode: action,
            saveEasyContactTicketingSettings: action,
            resetEasyContactTicketingSettings: action,

            hasCommunicationSettingsChanged: computed,
            hasValidationErrors: computed,
            hasKrediFlowSettingsChanged: computed,
            hasKrediFlowValidationErrors: computed,
            hasEcConfirmationMailChanged: computed,
            hasTicketBillingAddressChanged: computed,
            hasEasyContactTicketingSettingsChanged: computed,
            hasEasyContactTicketingValidationErrors: computed
        });
    }

    validateEmail = (email: string): string | undefined => {
        if (email.length > 0 && !Patterns.EMAILADDRESS.test(email)) {
            return i18next.t("screens.auth.invalid_email").toString();
        }
        return undefined;
    };

    updateGeneralEmail = (email: string) => {
        this.communicationSettings.generalEmail = email;
        this.generalEmailError = this.validateEmail(email);
    };

    updatePersonalEmail = (email: string) => {
        this.communicationSettings.personalEmail = email;
        this.personalEmailError = this.validateEmail(email);
    };

    saveCommunicationSettings = async () => {
        this.setIsLoadingCommunicationSettings(true);

        try {
            if (this.communicationSettings.generalEmail !== this.originalCommunicationSettings.generalEmail) {
                await apolloClientInstance.mutate<
                    UpdateCommunicationSettingsGeneralEmail,
                    UpdateCommunicationSettingsGeneralEmailVariables
                >({
                    mutation: UPDATE_COMMUNICATION_SETTINGS_GENERAL_EMAIL,
                    variables: {
                        customerId: this.communicationSettings.communicationSettingsId,
                        email: this.communicationSettings.generalEmail
                    }
                });
            }

            if (this.communicationSettings.personalEmail !== this.originalCommunicationSettings.personalEmail) {
                await this.rootStore.authStore.authClient.emailChange(this.communicationSettings.personalEmail);

                runInAction(() => {
                    this.rootStore.uiStore.printStatusMessage(
                        i18next.t("screens.auth.newemail.change.success"),
                        MessageType.SUCCESS,
                        10000
                    );
                });
            }

            // Sync original settings with current settings after successful save
            runInAction(() => {
                this.setOriginalCommunicationSettings({ ...this.communicationSettings });
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.success_save_message"),
                MessageType.SUCCESS
            );
        } catch (error) {
            console.error("Failed to save communication settings", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.failure_save_message"),
                MessageType.ERROR
            );
        } finally {
            runInAction(() => {
                this.setIsLoadingCommunicationSettings(false);
            });
        }
    };

    setOriginalCommunicationSettings = (communicationSettings: ICommunicationSettings) => {
        this.originalCommunicationSettings = communicationSettings;
    };

    resetCommunicationSettings = () => {
        this.communicationSettings = { ...this.originalCommunicationSettings };
        this.generalEmailError = undefined;
        this.personalEmailError = undefined;
    };

    get hasValidationErrors() {
        return this.generalEmailError !== undefined || this.personalEmailError !== undefined;
    }

    get hasCommunicationSettingsChanged() {
        return (
            this.communicationSettings.generalEmail !== this.originalCommunicationSettings.generalEmail ||
            this.communicationSettings.personalEmail !== this.originalCommunicationSettings.personalEmail
        );
    }

    loadCommunicationSettings = async () => {
        this.setIsLoadingCommunicationSettings(true);

        try {
            const result = await apolloClientInstance.query<GetCommunicationSettings>({
                query: GET_COMMUNICATION_SETTINGS,
                fetchPolicy: "network-only"
            });

            if (result.data.customer && result.data.customer.length > 0) {
                const customerData = result.data.customer[0];
                const personalEmail = this.rootStore.authStore.user?.username || "";
                const settings: ICommunicationSettings = {
                    communicationSettingsId: customerData.id,
                    generalEmail: customerData.email || "",
                    personalEmail: personalEmail
                };

                runInAction(() => {
                    this.setCommunicationSettings(settings);
                });
            }
        } catch (error) {
            console.error("Failed to load communication settings", error);
        } finally {
            runInAction(() => {
                this.setIsLoadingCommunicationSettings(false);
            });
        }
    };

    updateKrediFlowNotifyEmail = (email: string) => {
        this.krediFlowNotifyEmail = email;
        this.krediFlowNotifyEmailError = this.validateEmail(email);
    };

    loadKrediFlowSettings = async () => {
        this.isLoadingKrediFlowSettings = true;

        try {
            const result = await apolloClientInstance.query<GetKrediflowCommunicationSettings>({
                query: GET_KREDI_FLOW_COMMUNICATION_SETTINGS,
                fetchPolicy: "network-only"
            });

            const notifyEmail = result.data.notifyNoProcessedInvoiceToEmailAddress?.[0]?.value || "";

            runInAction(() => {
                this.krediFlowNotifyEmail = notifyEmail;
                this.originalKrediFlowNotifyEmail = notifyEmail;
            });
        } catch (error) {
            console.error("Failed to load KrediFlow communication settings", error);
        } finally {
            runInAction(() => {
                this.isLoadingKrediFlowSettings = false;
            });
        }
    };

    saveKrediFlowSettings = async () => {
        this.isLoadingKrediFlowSettings = true;

        try {
            const upsertObjects: { key: string; value: string; valuetype: number }[] = [];

            if (this.krediFlowNotifyEmail !== this.originalKrediFlowNotifyEmail) {
                upsertObjects.push({
                    key: "KREDIFLOW.NOTIFY_NO_PROCESSED_INVOICE_TO_EMAIL_ADDRESS",
                    value: this.krediFlowNotifyEmail,
                    valuetype: 1
                });
            }

            if (upsertObjects.length > 0) {
                await apolloClientInstance.mutate<UpsertCustomersetting, UpsertCustomersettingVariables>({
                    mutation: UPSERT_CUSTOMERSETTING,
                    variables: { objects: upsertObjects }
                });
            }

            runInAction(() => {
                this.originalKrediFlowNotifyEmail = this.krediFlowNotifyEmail;
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.success_save_message"),
                MessageType.SUCCESS
            );
        } catch (error) {
            console.error("Failed to save KrediFlow communication settings", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.failure_save_message"),
                MessageType.ERROR
            );
        } finally {
            runInAction(() => {
                this.isLoadingKrediFlowSettings = false;
            });
        }
    };

    resetKrediFlowSettings = () => {
        this.krediFlowNotifyEmail = this.originalKrediFlowNotifyEmail;
        this.krediFlowNotifyEmailError = undefined;
    };

    get hasKrediFlowSettingsChanged() {
        return this.krediFlowNotifyEmail !== this.originalKrediFlowNotifyEmail;
    }

    get hasKrediFlowValidationErrors() {
        return this.krediFlowNotifyEmailError !== undefined;
    }

    toggleSendEmailEcAndTicketing = () => {
        this.sendEmailEcAndTicketing = !this.sendEmailEcAndTicketing;
    };

    updateEcFallbackEmail = (email: string) => {
        this.ecFallbackEmail = email;
        this.ecFallbackEmailError = this.validateEmail(email);
    };

    updateEcConfirmationMail = (language: keyof IEcConfirmationMail, value: string) => {
        this.ecConfirmationMail[language] = value;
    };

    updateTicketBillingEmail = (email: string) => {
        this.ticketBillingEmail = email;
        this.ticketBillingEmailError = this.validateEmail(email);
    };

    updateTicketBillingAddress = (field: keyof ITicketBillingAddress, value: string) => {
        this.ticketBillingAddress[field] = value;
    };

    toggleTicketDemoMode = () => {
        this.ticketDemoMode = !this.ticketDemoMode;
    };

    loadEasyContactTicketingSettings = async () => {
        this.isLoadingEasyContactTicketingSettings = true;

        try {
            const result = await apolloClientInstance.query<GetEasycontactTicketingCommunicationSettings>({
                query: GET_EASYCONTACT_TICKETING_COMMUNICATION_SETTINGS,
                fetchPolicy: "network-only"
            });

            const sendEmail = result.data.sendEmailEcAndTicketing?.[0]?.value === "true";
            const fallbackEmail = result.data.ecEmailInbox?.[0]?.value || "";

            const brToNewline = (val: string | null | undefined) => (val || "").replaceAll("<br>", "\n");
            const confirmationMail: IEcConfirmationMail = {
                de: brToNewline(result.data.ecConfirmationMailDE?.[0]?.value),
                en: brToNewline(result.data.ecConfirmationMailEN?.[0]?.value),
                fr: brToNewline(result.data.ecConfirmationMailFR?.[0]?.value),
                it: brToNewline(result.data.ecConfirmationMailIT?.[0]?.value)
            };

            const billingEmail = result.data.ticketBillingEmail?.[0]?.value || "";

            let billingAddress: ITicketBillingAddress = { ...DEFAULT_TICKET_BILLING_ADDRESS };
            const billingAddressRaw = result.data.ticketBillingAddress?.[0]?.value;
            if (billingAddressRaw) {
                try {
                    const parsed = JSON.parse(billingAddressRaw);
                    billingAddress = {
                        name: parsed.name || "",
                        co: parsed["c/o"] || "",
                        city: parsed.city || "",
                        zip: parsed.zip || "",
                        street: parsed.street || "",
                        housenumber: parsed.housenumber || ""
                    };
                } catch (error) {
                    console.error("Failed to parse billing address JSON", error);
                }
            }

            runInAction(() => {
                this.sendEmailEcAndTicketing = sendEmail;
                this.originalSendEmailEcAndTicketing = sendEmail;
                this.ecFallbackEmail = fallbackEmail;
                this.originalEcFallbackEmail = fallbackEmail;
                this.ecConfirmationMail = { ...confirmationMail };
                this.originalEcConfirmationMail = { ...confirmationMail };
                this.ticketBillingEmail = billingEmail;
                this.originalTicketBillingEmail = billingEmail;
                this.ticketBillingAddress = { ...billingAddress };
                this.originalTicketBillingAddress = { ...billingAddress };
                this.ticketDemoMode = result.data.ticketDemoMode?.[0]?.value === "true";
                this.originalTicketDemoMode = this.ticketDemoMode;
            });
        } catch (error) {
            console.error("Failed to load EasyContact/Ticketing communication settings", error);
        } finally {
            runInAction(() => {
                this.isLoadingEasyContactTicketingSettings = false;
            });
        }
    };

    saveEasyContactTicketingSettings = async () => {
        this.isLoadingEasyContactTicketingSettings = true;

        try {
            const upsertObjects: { key: string; value: string; valuetype: number }[] = [];

            if (this.sendEmailEcAndTicketing !== this.originalSendEmailEcAndTicketing) {
                upsertObjects.push({
                    key: "EC.SEND_EMAIL_IN_EC_AND_TICKETING",
                    value: this.sendEmailEcAndTicketing.toString(),
                    valuetype: 1
                });
            }

            if (this.ecFallbackEmail !== this.originalEcFallbackEmail) {
                upsertObjects.push({
                    key: "EC.EMAILINBOX",
                    value: this.ecFallbackEmail,
                    valuetype: 1
                });
            }

            const confirmationMailKeys: { lang: keyof IEcConfirmationMail; key: string }[] = [
                { lang: "de", key: "EC.CONFIRMATIONMAIL_DE" },
                { lang: "en", key: "EC.CONFIRMATIONMAIL_EN" },
                { lang: "fr", key: "EC.CONFIRMATIONMAIL_FR" },
                { lang: "it", key: "EC.CONFIRMATIONMAIL_IT" }
            ];

            for (const { lang, key } of confirmationMailKeys) {
                if (this.ecConfirmationMail[lang] !== this.originalEcConfirmationMail[lang]) {
                    upsertObjects.push({
                        key,
                        value: this.ecConfirmationMail[lang].replaceAll("\n", "<br>"),
                        valuetype: 3
                    });
                }
            }

            if (this.ticketBillingEmail !== this.originalTicketBillingEmail) {
                upsertObjects.push({
                    key: "TICKET.BILLINGEMAIL",
                    value: this.ticketBillingEmail,
                    valuetype: 1
                });
            }

            if (this.hasTicketBillingAddressChanged) {
                const addressJson = JSON.stringify({
                    name: this.ticketBillingAddress.name,
                    "c/o": this.ticketBillingAddress.co,
                    city: this.ticketBillingAddress.city,
                    zip: this.ticketBillingAddress.zip,
                    street: this.ticketBillingAddress.street,
                    housenumber: this.ticketBillingAddress.housenumber
                });
                upsertObjects.push({
                    key: "TICKET.BILLINGADDRESS",
                    value: addressJson,
                    valuetype: 3
                });
            }

            if (this.ticketDemoMode !== this.originalTicketDemoMode) {
                upsertObjects.push({
                    key: "TICKET.DEMOMODE",
                    value: this.ticketDemoMode.toString(),
                    valuetype: 3
                });
            }

            if (upsertObjects.length > 0) {
                await apolloClientInstance.mutate<UpsertCustomersetting, UpsertCustomersettingVariables>({
                    mutation: UPSERT_CUSTOMERSETTING,
                    variables: { objects: upsertObjects }
                });
            }

            runInAction(() => {
                this.originalSendEmailEcAndTicketing = this.sendEmailEcAndTicketing;
                this.originalEcFallbackEmail = this.ecFallbackEmail;
                this.originalEcConfirmationMail = { ...this.ecConfirmationMail };
                this.originalTicketBillingEmail = this.ticketBillingEmail;
                this.originalTicketBillingAddress = { ...this.ticketBillingAddress };
                this.originalTicketDemoMode = this.ticketDemoMode;
            });

            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.success_save_message"),
                MessageType.SUCCESS
            );
        } catch (error) {
            console.error("Failed to save EasyContact/Ticketing communication settings", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.communication_settings.failure_save_message"),
                MessageType.ERROR
            );
        } finally {
            runInAction(() => {
                this.isLoadingEasyContactTicketingSettings = false;
            });
        }
    };

    resetEasyContactTicketingSettings = () => {
        this.sendEmailEcAndTicketing = this.originalSendEmailEcAndTicketing;
        this.ecFallbackEmail = this.originalEcFallbackEmail;
        this.ecFallbackEmailError = undefined;
        this.ecConfirmationMail = { ...this.originalEcConfirmationMail };
        this.ticketBillingEmail = this.originalTicketBillingEmail;
        this.ticketBillingEmailError = undefined;
        this.ticketBillingAddress = { ...this.originalTicketBillingAddress };
        this.ticketDemoMode = this.originalTicketDemoMode;
    };

    get hasEcConfirmationMailChanged() {
        return (
            this.ecConfirmationMail.de !== this.originalEcConfirmationMail.de ||
            this.ecConfirmationMail.en !== this.originalEcConfirmationMail.en ||
            this.ecConfirmationMail.fr !== this.originalEcConfirmationMail.fr ||
            this.ecConfirmationMail.it !== this.originalEcConfirmationMail.it
        );
    }

    get hasTicketBillingAddressChanged() {
        return (
            this.ticketBillingAddress.name !== this.originalTicketBillingAddress.name ||
            this.ticketBillingAddress.co !== this.originalTicketBillingAddress.co ||
            this.ticketBillingAddress.city !== this.originalTicketBillingAddress.city ||
            this.ticketBillingAddress.zip !== this.originalTicketBillingAddress.zip ||
            this.ticketBillingAddress.street !== this.originalTicketBillingAddress.street ||
            this.ticketBillingAddress.housenumber !== this.originalTicketBillingAddress.housenumber
        );
    }

    get hasEasyContactTicketingSettingsChanged() {
        return (
            this.sendEmailEcAndTicketing !== this.originalSendEmailEcAndTicketing ||
            this.ecFallbackEmail !== this.originalEcFallbackEmail ||
            this.hasEcConfirmationMailChanged ||
            this.ticketBillingEmail !== this.originalTicketBillingEmail ||
            this.hasTicketBillingAddressChanged ||
            this.ticketDemoMode !== this.originalTicketDemoMode
        );
    }

    get hasEasyContactTicketingValidationErrors() {
        return this.ecFallbackEmailError !== undefined || this.ticketBillingEmailError !== undefined;
    }

    setIsLoadingCommunicationSettings = (isLoading: boolean) => {
        this.isLoadingCommunicationSettings = isLoading;
    };

    setCommunicationSettings = (communicationSettings: ICommunicationSettings) => {
        this.communicationSettings = communicationSettings;
        this.originalCommunicationSettings = { ...communicationSettings };
    };
}
